/**
 * Full SSR (treatment) entry for the artwork detail route. Mounted
 * via `dynamicWithExperiment` from `routes/_app.artwork.$idSlug.tsx`
 * whenever the global `SSR_DUAL_MODE_EXPERIMENT_NAME` experiment
 * resolves to `treatment`.
 *
 * Three render paths, in priority order:
 * 1. Hard error (`ssrLoadError`, e.g. invalid route param, archived
 *    artwork) → `<AppErrorState />`.
 * 2. SSR seed available (`artworkApollo`) → route-scoped Apollo
 *    client pinned by `artworkId`, seeded with the SSR-prefetched
 *    `GetArtworkWithTaskDetail` payload + the deferred
 *    author-artworks cache seeder.
 * 3. Soft failure (`!artworkApollo`, no `ssrLoadError`) → bare
 *    route-scoped Apollo client (no seeds) + `<ArtworkDetailPage>`,
 *    letting the client-side useQuery hooks re-fetch with the user's
 *    credentials cookie. This keeps logged-in callers able to view
 *    unlisted / draft / private artworks (which anonymous server-side
 *    GraphQL cannot see) without flipping the page into
 *    `<AppErrorState />`. See the comment on the soft-failure branch
 *    below for the full rationale; mirrors the analogous path in
 *    `_app.model.$modelIdSlug.($versionId).full-ssr.tsx`.
 *
 * The CSR (control) entry lives in `_app.artwork.$idSlug.csr.tsx`
 * and ignores all of these branches; it relies on the global
 * `RootRuntime` Apollo client + `useGetArtworkWithTaskDetail` for
 * data fetching.
 */
import type { ApolloClient } from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { Suspense, use, useRef } from 'react'
import { useLoaderData, useParams } from 'react-router'
import AppErrorState from 'src/components/AppErrorState'
import { Component as ArtworkDetailPage } from 'src/pages/ArtworkDetailPage'
import type { loader } from 'src/routes/_app.artwork.$idSlug'
import {
  GetArtworkWithTaskDetailDocument,
  ListArtworksDocument,
  type ListArtworksQuery,
  type ListArtworksQueryVariables,
} from 'src/services/apollo/generated/graphql'
import { createRouteApolloClient } from 'src/services/apollo/route-ssr-client'
import { useRequestContext } from 'src/utils/request-context'
import { hasSsrLoaderError } from 'src/utils/ssr-loader-error'

export default function ArtworkDetailFullSSREntry() {
  const loaderData = useLoaderData<typeof loader>()
  const params = useParams()
  const { queryClient } = useRequestContext()
  const ssrLoadError = hasSsrLoaderError(loaderData)
    ? loaderData.ssrLoadError
    : null
  const artworkApollo =
    !ssrLoadError && 'artworkApollo' in loaderData
      ? loaderData.artworkApollo
      : undefined

  // Pin the route-scoped Apollo client by `artworkId`, not by the
  // `artworkApollo` object reference. React Router 7 framework mode
  // re-deserialises `loaderData` after hydration completes (and again
  // when `clientLoader.hydrate` resolves its `args.serverLoader()`
  // promise), which produces a **deep-equal but reference-different**
  // `artworkApollo` object on every pass. With `useMemo([artworkApollo])`
  // that flips us through `client A (SSR) -> client B (hydrate render
  // 1) -> client C (after clientLoader resolves)`, and each new client
  // ships a freshly-built `InMemoryCache` whose `useQuery` subscribers
  // briefly observe `loading: true` before `cache.writeQuery(seed)`
  // settles — exactly the "SSR shows real images, hydrate flashes
  // skeletons, post-hydrate restores images" sequence the user
  // reported on `/<lang>/artwork/:idSlug`.
  //
  // `useRef` keyed by `artworkId` keeps a single client / cache pair
  // alive for the lifetime of *that* artwork's route. SPA-navigating
  // to a different artwork swaps the id and rebuilds the client (so
  // we don't leak the previous artwork's cache entries).
  const clientRef = useRef<{
    id: string
    client: ApolloClient
  } | null>(null)
  if (ssrLoadError) {
    return <AppErrorState errorDetail={ssrLoadError.detail} />
  }
  const artworkId = artworkApollo?.artworkId ?? null
  if (artworkApollo && artworkId && clientRef.current?.id !== artworkId) {
    // Only the *primary* artwork query goes into the seed list at
    // mount time; the author-artworks list is now deferred and gets
    // written into the same client by `<AuthorArtworksDeferredSeeder>`
    // once its promise resolves (see below).
    const seeds: Parameters<typeof createRouteApolloClient>[0] = [
      {
        query: GetArtworkWithTaskDetailDocument,
        variables: { id: artworkApollo.artworkId },
        data: artworkApollo.artworkData,
      },
    ]
    clientRef.current = {
      id: artworkId,
      client: createRouteApolloClient(seeds),
    }
  }

  if (artworkApollo && clientRef.current?.client) {
    const client = clientRef.current.client
    return (
      <QueryClientProvider client={queryClient}>
        <ApolloProvider client={client}>
          <ArtworkDetailPage
            initialArtwork={artworkApollo.artworkData.artwork}
          />
          {/*
            Deferred author-artworks cache seeder. The loader returned a
            promise (`authorArtworksPromise`) instead of an awaited
            object, so the main loader settles ~200-500ms earlier and
            the hero / title / tags reach the user faster. The seeder
            renders nothing — it only suspends until the promise
            resolves and then writes the connection into the route's
            Apollo client, which causes
            `<ArtworkDetailAuthorArtworks>`'s `usePagedListArtworks`
            subscriber to swap from its skeleton to the real cards.
            On `<Suspense fallback={null}>` we deliberately render
            nothing while pending — `<PersonalArtworkList>` already
            renders its own skeleton row in the page body, so an extra
            fallback here would be visual noise.
          */}
          {artworkApollo.authorArtworksVariables ? (
            <Suspense fallback={null}>
              <AuthorArtworksDeferredSeeder
                client={client}
                variables={artworkApollo.authorArtworksVariables}
                promise={artworkApollo.authorArtworksPromise}
              />
            </Suspense>
          ) : null}
        </ApolloProvider>
      </QueryClientProvider>
    )
  }

  // Soft-failure path: SSR loader couldn't get an artwork (e.g.
  // anonymous server fetch can't see an unlisted draft, or the
  // upstream call hit a transient 5xx). Mount a *bare* route-scoped
  // Apollo client (no seeds) and let `<ArtworkDetailPage>` itself
  // drive the fetch through Apollo hooks. SSR + first hydrate both
  // render the page's loading state (cache is empty in both), which
  // keeps the DOM structurally identical between the two passes —
  // React happily hydrates without throwing the SSR tree away. Once
  // hydration commits, the client-side useQuery fires through the
  // route client's HttpLink (which already sends
  // `credentials: 'include'`), the user_token cookie reaches the
  // GraphQL server, and the owner gets the same content they'd see
  // under Stage 1 CSR. Anonymous visitors land on the page's
  // existing not-found state instead of the SSR `<AppErrorState/>`.
  //
  // Why not render the CSR entry directly: doing so would mount
  // `<ArtworkDetailPage>` outside of any `<ApolloProvider>` — the
  // `<RootRuntime>` Apollo provider is bypassed for SSR-shell
  // routes, so Apollo hooks would crash. Building a route-scoped
  // client here is the smallest surface that still keeps the page
  // alive end-to-end.
  //
  // Pin the bare client by `idSlug` so SPA navigations between two
  // soft-failure artwork URLs still rebuild the cache (avoids
  // serving artwork A's stale `useGetArtworkWithTaskDetail` result
  // to artwork B's render).
  const bareKey = `bare:${params.idSlug ?? ''}`
  if (clientRef.current?.id !== bareKey) {
    clientRef.current = { id: bareKey, client: createRouteApolloClient([]) }
  }
  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={clientRef.current.client}>
        <ArtworkDetailPage />
      </ApolloProvider>
    </QueryClientProvider>
  )
}

/**
 * Internal component that consumes the loader's deferred
 * `authorArtworksPromise` and writes the result into the route's
 * Apollo client. Renders `null` either way — its only job is the
 * cache write.
 *
 * `use(promise)` integrates with React 19 Suspense + React Router 7
 * streaming: SSR render suspends here until either the promise
 * resolves (in which case the connection is in the SSR HTML's
 * Apollo cache snapshot via the seeder) or shell-flush time hits;
 * post-hydrate, the React Router runtime feeds the same promise to
 * the client and `use()` resumes there, performing the write
 * client-side.
 */
function AuthorArtworksDeferredSeeder({
  client,
  variables,
  promise,
}: {
  client: ApolloClient
  variables: ListArtworksQueryVariables
  promise: Promise<ListArtworksQuery['artworks'] | null>
}) {
  const connection = use(promise)
  if (connection) {
    client.writeQuery({
      query: ListArtworksDocument,
      variables,
      data: { artworks: connection } satisfies ListArtworksQuery,
    })
  }
  return null
}
